import {
  IMAncillaryContent,
  IMAssessment,
  IMCourse,
  IMLesson,
  IMUnit,
} from '../types'
import { createNodeList, createUnit } from '../../nodeCreationHelpers'
import { parseAssessment } from './assessment'
import { parseUnitSections } from './section'
import { ancillaryItemsToString } from './ancillary_content'
import {
  createTable,
  createUnorderedList,
  wrapB,
  wrapP,
  wrapTag,
} from '../../html_utility'
import { suggestedCenters } from './center'
import { createItemGlossary } from './glossary'
import { filteredNoteNodes } from './utility'
import { IM360Parser } from './IM360Parser'
import { unitFamilySupportMaterials } from './section/familySupportMaterials'
import { ISimplifiedNode } from '../../../components/ingestion/IngestedTree'
import { createDocumentNode } from './createNodeWrappers'

export function parseUnits(imCourse: IMCourse) {
  return imCourse.units.map((unit) => {
    const title = `${unit.ordinal_title}: ${unit.title}`
    return createUnit(title, unitChildren(unit, imCourse))
  })
}

function unitChildren(unit: IMUnit, course: IMCourse) {
  const childNotes: [string, string | undefined][] = [
    ['Goals', ancillaryItemsToString(undefined, unit.ancillary_content?.goals)],
    ['Unit Narrative', unit.long_narrative],
    ['Throughout the Unit', unit.throughout_the_unit],
  ]
  return [
    ...filteredNoteNodes(...childNotes),
    materialsNeeded(unit),
    ...unitFamilySupportMaterials(unit),
    ...suggestedCenters(unit),
    ...assessmentNodesForCategory(unit, 'pre', 'Pre-Assessment'),
    parseUnitSections({
      unitOrdinalTitle: unit.ordinal_title,
      unitFamilySupport: unit.family_support_materials,
      sections: unit.sections,
      modelingPrompts: course.modeling_prompts,
      midAssessments: midUnitAssessmentsByLessonSlug(unit),
    }),
    ...assessmentNodesForCategory(unit, 'end'),
    ...createItemGlossary(
      unit.ancillary_content,
      `${unit.ordinal_title}: Glossary`,
    ),
  ]
}

function unitMaterialsToGather(unit: IMUnit) {
  const materials = unit.sections.reduce<Set<string>>((acc, { lessons }) => {
    lessons.forEach((lesson) => {
      lessonMaterials('required_materials', lesson).forEach((item) =>
        acc.add(item),
      )
      lesson.activities?.forEach(({ ancillary_content }) => {
        ancillary_content.required_materials?.forEach(({ title }) =>
          acc.add(title),
        )
      })
    })
    return acc
  }, new Set())
  return createUnorderedList(Array.from(materials).sort())
}

function materialsNeeded(unit: IMUnit) {
  return createNodeList({ title: 'Materials Needed' }, [
    createDocumentNode('Materials Needed', [
      IM360Parser.getInstance().createNoteNode({
        title: 'Materials To Gather',
        studentFacing: false,
        value: unitMaterialsToGather(unit),
      }),
      IM360Parser.getInstance().createNoteNode({
        title: 'Materials Needed',
        studentFacing: false,
        value: materialsTable(unit),
      }),
    ]),
  ])
}

function lessonMaterialsList(
  key: 'required_materials' | 'materials_to_copy',
  lesson: IMLesson,
) {
  return createUnorderedList(lessonMaterials(key, lesson, true))
}

function ancillaryList(
  key: 'required_materials' | 'materials_to_copy',
  ancillaryContent: IMAncillaryContent,
  postString?: string,
) {
  return (
    ancillaryContent[key]?.map(({ title }) => {
      const parts = [title]
      if (postString) parts.push(postString)
      return parts.join(': ')
    }) ?? []
  )
}

function lessonMaterials(
  key: 'required_materials' | 'materials_to_copy',
  lesson: IMLesson,
  addLocTitle: boolean = false,
) {
  return [
    ...ancillaryList(
      key,
      lesson.ancillary_content ?? {},
      addLocTitle ? 'Lesson' : '',
    ),
    ...(lesson.activities?.reduce<string[]>((acc, activity) => {
      return [
        ...acc,
        ...ancillaryList(
          key,
          activity.ancillary_content,
          addLocTitle ? activity.ordinal_title : '',
        ),
      ]
    }, []) ?? []),
  ]
}

function materialsTable(unit: IMUnit) {
  const boldStyle = (content: string) =>
    wrapP(wrapB(content), { style: 'padding: 15px' })
  const tableData = unit.sections.reduce<string[][]>(
    (acc, section) => {
      section.lessons.forEach((lesson) => {
        acc.push([
          boldStyle(wrapTag('nobr', lesson.ordinal_title)),
          lessonMaterialsList('required_materials', lesson),
          lessonMaterialsList('materials_to_copy', lesson),
        ])
      })
      return acc
    },
    [
      ['Lesson', 'Materials to Gather', 'Materials to Copy'].map((title) =>
        boldStyle(title),
      ),
    ],
  )
  return createTable(tableData)
}

function assessmentTitle(assessment: IMAssessment) {
  if (assessment.version) {
    return `${assessment.title} (${assessment.version})`
  }
  return assessment.title
}

function unitAssessment(assessment: IMAssessment) {
  return parseAssessment(assessment, {
    title: assessmentTitle(assessment),
    assessment_grading: 'summative',
  })
}

function assessmentNodesForCategory(
  unit: IMUnit,
  category: 'pre' | 'end',
  title: string = 'Assessments',
) {
  const parsed = assessmentsForCategory(unit, category).map(unitAssessment)
  if (!parsed.length) return []
  return [createNodeList({ title }, parsed)]
}

function assessmentsForCategory(
  { assessments }: IMUnit,
  assessmentCategory: 'pre' | 'end' | 'mid',
) {
  return assessments.filter(({ category }) => category === assessmentCategory)
}

function getLessonSlug(assessment: IMAssessment) {
  if (assessment.references?.length) {
    // Ideally we can find the lesson slug in the references
    return [...assessment.references].pop()?.slug
  }
  // If not in references we try to parse it out of the teacher instructions
  const regResult = /[Ll]esson (\d{1,2})\./.exec(
    assessment.teacher_instructions ?? '',
  )
  if (regResult?.length) {
    return `lesson-${regResult[1]}`
  }
  // If we can't find the lesson number get the lesson title
  const otherRegRes = /[Ll]esson ["'](.*?)["']/.exec(
    assessment.teacher_instructions ?? '',
  )
  if (otherRegRes?.length) {
    return otherRegRes[1].replace('.', '')
  }
}

function midUnitAssessmentsByLessonSlug(unit: IMUnit) {
  const assessments = assessmentsForCategory(unit, 'mid')
  return assessments.reduce<Record<string, ISimplifiedNode[]>>(
    (acc, assessment) => {
      const lessonSlug = getLessonSlug(assessment)
      if (!lessonSlug) {
        // This seems to happen in courses that are not complete, not much we can do here?
        return acc
      }
      if (!(lessonSlug in acc)) {
        acc[lessonSlug] = []
      }
      acc[lessonSlug].push(unitAssessment(assessment))
      return acc
    },
    {},
  )
}
